import { type FC, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Button from "../../../../../../components/button/button";
import Input from "../../../../../../components/input/input";
import { usePollVotes } from "../../../../../../hooks/use-poll-votes";
import { openXShareIntent } from "../../../../../../lib/x-share";
import { endPollRequest } from "../../../../../../redux/broadcast/broadcast.actions";
import { selectBroadcastId } from "../../../../../../redux/broadcast/broadcast.selectors";
import type { Poll as PollType } from "../../../../../../types/polls.types";
import UpdatesSocial from "../../../updates-module/components/social/updates-social";
import useSocialShare from "../../../updates-module/hooks/use-social-share";
import PollOption from "../poll-option/poll-option";
import * as CSS from "./poll.styles";

type PollProps = {
	poll: PollType;
};

const Poll: FC<PollProps> = ({ poll }) => {
	const dispatch = useDispatch();
	const broadcastId = useSelector(selectBroadcastId);
	const [commentText, setCommentText] = useState("");
	const { totalVotes, countsByOption } = usePollVotes(broadcastId, poll.id);
	const social = useSocialShare();

	const handleEndPoll = () => {
		if (!broadcastId) return;

		const comment = commentText?.trim();

		dispatch(
			endPollRequest({
				broadcastId,
				pollId: poll.id,
				comment: comment || undefined,
				share: social.blueSky,
				hashtags: social.parsedHashtags,
			}),
		);

		if (social.postToX) {
			openXShareIntent(
				[`📊 Poll: "${poll.question}" has ended!`, comment]
					.filter(Boolean)
					.join("\n"),
				broadcastId,
				social.parsedHashtags,
			);
		}

		setCommentText("");
		social.reset();
	};

	return (
		<CSS.Poll $hideSeperator={false}>
			<CSS.PollQuestion>{poll.question}</CSS.PollQuestion>
			{poll.options.map((option) => (
				<PollOption
					key={option}
					option={option}
					votes={countsByOption[option] ?? 0}
					totalVotes={totalVotes}
				/>
			))}
			<CSS.InputContainer>
				<Input
					value={commentText}
					onChange={(e) => setCommentText(e.target.value)}
					placeholder="Type your comment"
				/>
			</CSS.InputContainer>
			<div
				style={{
					display: "flex",
					alignItems: "flex-end",
					marginTop: "1rem",
				}}
			>
				<UpdatesSocial
					blueSky={social.blueSky}
					onBlueSkyChange={social.setBlueSky}
					postToX={social.postToX}
					onPostToXChange={social.setPostToX}
					hashtags={social.hashtags}
					onHashtagsChange={social.setHashtags}
				/>
				<Button onClick={handleEndPoll}>End poll</Button>
			</div>
		</CSS.Poll>
	);
};

export default Poll;
